import React, { useEffect, useState } from "react";
import "../CI/COD.css";
import { getRequest } from "../../globalhelper/helper";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../redux/Common/commonActions";
import { CSVLink } from "react-csv";

const BillAnalysisAndStatusReport = () => {
  const [formData, setFormData] = useState({
    fromDate: "",
    toDate: "",
    company: "",
  });

  const [companies, setCompanies] = useState([]);
  const [queryResult, setQueryResult] = useState([]);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const dispatch = useDispatch();

  useEffect(() => {
    getRequest(`api/edureka/getCompanyAssignedToUser`).then(({ res }) => {
      setCompanies(
        (res.data || []).sort((a, b) =>
          (a.orgname || "").localeCompare(b.orgname || "")
        )
      );
      if (res.data && res.data.length === 1) {
        setFormData((prev) => ({ ...prev, company: res.data[0] }));
      }
    }).catch((err) => console.log(err));

    const currentDate = moment();
    setFormData((prev) => ({
      ...prev,
      fromDate: currentDate.clone().startOf("month").format("YYYY-MM-DD"),
      toDate: currentDate.clone().endOf("month").format("YYYY-MM-DD"),
    }));
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    const currentDate = moment();
    setFormData({
      company: "",
      fromDate: currentDate.clone().startOf("month").format("YYYY-MM-DD"),
      toDate: currentDate.clone().endOf("month").format("YYYY-MM-DD"),
    });
    setQueryResult([]);
    setShowError(false);
  };

  const handleSubmit = () => {
    if (!formData.fromDate || !formData.toDate || !formData.company) return;

    dispatch(showLoader());
    setShowError(false);

    getRequest(
      `api/edureka/billAnalysisAndStatusReport?startDate=${formData.fromDate}&endDate=${formData.toDate}&company=${formData.company.orgid}`
    )
      .then((response) => {
        const responseData = JSON.parse(response.res.data);
        if (responseData.length) {
          setQueryResult(responseData);
        } else {
          setShowError(true);
          setErrorMessage("No data found, please try some other combination");
        }
      })
      .catch((err) => {
        console.log(err);
        setShowError(true);
        setErrorMessage("Oops something went wrong!!");
      })
      .finally(() => dispatch(hideLoader()));
  };

  return (
    <>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            Please select the date range to view Reports
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container gap={2} spacing={3}>
            {/* Company Autocomplete */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                fullWidth
                value={formData.company || null}
                options={companies || []}
                getOptionLabel={(option) => option?.orgname || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.orgid === value?.orgid
                }
                onChange={(event, newValue) => handleChange("company", newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company Name"
                    variant="outlined"
                  />
                )}
              />
            </Grid>

            {/* From Date */}
            <Grid item xs={12} sm={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="From"
                  views={["year", "month", "day"]}
                  value={formData.fromDate ? moment(formData.fromDate) : null}
                  inputFormat="DD-MM-YYYY"
                  minDate={moment().subtract(10, "year").startOf("year")}
                  maxDate={moment().endOf("year")}
                  onChange={(newValue) =>
                    handleChange(
                      "fromDate",
                      newValue ? newValue.format("YYYY-MM-DD") : ""
                    )
                  }
                  renderInput={(params) => (
                    <TextField fullWidth size="small" {...params} />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            {/* To Date */}
            <Grid item xs={12} sm={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="To"
                  views={["year", "month", "day"]}
                  inputFormat="DD-MM-YYYY"
                  value={formData.toDate ? moment(formData.toDate) : null}
                  minDate={moment().subtract(10, "year").startOf("year")}
                  maxDate={moment().endOf("year")}
                  onChange={(newValue) =>
                    handleChange(
                      "toDate",
                      newValue ? newValue.format("YYYY-MM-DD") : ""
                    )
                  }
                  renderInput={(params) => (
                    <TextField fullWidth size="small" {...params} />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            {/* Buttons inline */}
            <Grid item xs={6} sm={6} md={1}>
              <Button
                color="primary"
                variant="contained"
                onClick={handleSubmit}
                disabled={
                  !formData.fromDate || !formData.toDate || !formData.company
                }
              >
                Submit
              </Button>
              <Button
                color="error"
                onClick={handleReset}
                style={{ marginLeft: "10px" }}
                variant="contained"
              >
                Reset
              </Button>
              {queryResult.length ? (
                <Button color="success" variant="contained" style={{ marginLeft: "10px" }}>
                  <CSVLink
                    data={queryResult}
                    filename="Bill Analysis and Status Report.csv"
                    style={{ color: "white", textDecoration: "none" }}
                  >
                    Download
                  </CSVLink>
                </Button>
              ) : null}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Table */}
      {queryResult.length > 0 ? (
        <TableContainer component={Paper} style={{ marginTop: "20px" }}>
          <Table>
            <TableHead>
              <TableRow>
                {Object.keys(queryResult[0]).map((item, index) => (
                  <TableCell key={index}>{item}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {queryResult.map((item, index) => (
                <TableRow key={index}>
                  {Object.keys(item).map((val, i) => (
                    <TableCell key={i}>{item[val]}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ margin: 'auto', mt: 3, width: '95%' }}>
          <Alert severity='warning'>
            <AlertTitle>No Data Found</AlertTitle>
            There is no data to display at the moment.
          </Alert>
        </Box>
      )}

      {showError && <div className='center-screen'>{errorMessage}</div>}
    </>
  );
};

export default BillAnalysisAndStatusReport;
